/**
 * Which way an outfit faces (gyro-svc mascotFacing.ts; owner, 2026-10-15): every outfit faces left. The service judges
 * each upload with its model and stores one drawn facing right mirrored; the console shows what was decided, lets an
 * admin flip or undo a flip, and runs the backfill over the outfits uploaded before this. The words here are the
 * card's (product words: outfit, mascot, "faces left").
 */
import type { AdminOutfit } from './desktopMascot';

export type OutfitFacing = 'left' | 'right' | 'front' | 'unclear';
export type FacingSource = 'model' | 'admin';
export type FacingNote = 'model_failed' | 'model_refused' | 'mirror_duplicate' | 'mirror_failed';

/** An outfit's facing as the service answers it on the catalog (AdminOutfit.facing); absent until the outfit has been judged. */
export interface OutfitFacingView {
  facing: OutfitFacing;
  /** The stored image is the mirror of what was uploaded (so it faces left). */
  mirrored: boolean;
  facingSource: FacingSource;
  note: FacingNote | null;
}

export interface FacingSummary {
  total: number;
  judged: number;
  unjudged: number;
  counts: Record<OutfitFacing, number>;
  mirrored: number;
  /** Who decided the judged outfits: the model (the backfill can judge these again) or an admin (final). Absent from a service before scopes. */
  judgedBy?: { model: number; admin: number };
  unclear: Array<{ id: string; name: string; note: FacingNote | null }>;
  notMirrored: Array<{ id: string; name: string; note: FacingNote | null }>;
  spend: { day: string; usd: number; calls: number; capUsd: number };
  model: string;
}

export interface BackfillCounts {
  scanned: number;
  classified: number;
  mirrored: number;
  left: number;
  right: number;
  front: number;
  unclear: number;
  failed: number;
}

/**
 * Which outfits a backfill run judges (gyro-svc mascotFacingRoutes.ts backfillSelects): the ones never judged (safe to
 * run again), the model's unclear or failed ones, the ones the model judged front, or everything the model judged. An
 * outfit an admin flipped, undid or marked is never judged again.
 */
export type BackfillScope = 'unjudged' | 'unclear' | 'front' | 'all';
export const BACKFILL_SCOPES: ReadonlyArray<{ value: BackfillScope; label: string }> = [
  { value: 'unjudged', label: 'Not yet judged' },
  { value: 'unclear', label: 'Unclear only' },
  { value: 'front', label: 'Judged front by the model' },
  { value: 'all', label: 'Everything the model judged' },
];

export interface BackfillAnswer {
  /** Absent from a service before scopes (it judged the outfits not yet judged). */
  scope?: BackfillScope;
  cursor: string | null;
  done: boolean;
  counts: BackfillCounts;
  total: number;
  remaining: number;
  stopped?: { reason: 'rate' | 'budget' | 'storage' | 'audit'; retryAfterSec: number };
  spend: { day: string; usd: number; calls: number; capUsd: number };
  model: string;
}

export type FacingAction = 'flip' | 'undo' | 'mark-left' | 'mark-front' | 'judge';

export interface FacingActionAnswer {
  outfitId: string;
  facing: OutfitFacingView;
  sha256: string;
}

export const FACING_CONVENTION_TEXT = 'Every outfit faces left, as the built-in Gyro does; desktops mirror the whole picture for a person who chose “Gyro faces right”.';
export const FACING_BACKFILL_HINT = 'Judges outfits 25 at a time, each next to Gyro’s own left-facing art, and mirrors the ones facing right so they face left. Choose which outfits: the ones not judged yet (safe to run again), the unclear ones, the ones the model judged front, or everything the model judged. An outfit an admin flipped, undid or marked is never judged again, and an outfit mirrored before is judged as it is now, never mirrored twice.';

/** How many outfits a scope would judge, from the summary: exact for the ones not yet judged, an upper bound for the rest (the admin-judged among them are skipped). */
export function backfillScopeCount(summary: FacingSummary, scope: BackfillScope): { count: number; exact: boolean } {
  switch (scope) {
    case 'unjudged': return { count: summary.unjudged, exact: true };
    case 'unclear': return { count: summary.counts.unclear, exact: false };
    case 'front': return { count: summary.counts.front, exact: false };
    default: return { count: summary.judgedBy?.model ?? summary.judged, exact: summary.judgedBy !== undefined };
  }
}

/** The button's words for a scope: "Run facing backfill (81 to judge)" or "(up to 61 to judge again)". */
export function backfillButtonText(summary: FacingSummary, scope: BackfillScope): string {
  const { count, exact } = backfillScopeCount(summary, scope);
  if (count === 0) return 'Run facing backfill';
  const n = count.toLocaleString('en-US');
  return scope === 'unjudged' ? `Run facing backfill (${n} to judge)` : `Run facing backfill (${exact ? n : `up to ${n}`} to judge again)`;
}

/** The note when a run finishes, by scope. */
export function backfillDoneText(scope: BackfillScope): string {
  switch (scope) {
    case 'unjudged': return 'Every outfit has been judged.';
    case 'unclear': return 'Every unclear outfit has been judged again.';
    case 'front': return 'Every outfit the model judged front has been judged again.';
    default: return 'Every outfit the model judged has been judged again.';
  }
}

/** The one word a facing shows as: the column, the chip and the review list use it. */
export function facingLabel(view: OutfitFacingView | undefined): string {
  if (!view) return 'Not checked yet';
  if (view.mirrored) return view.facingSource === 'admin' ? 'Flipped to face left' : 'Faced right → mirrored';
  switch (view.facing) {
    case 'left': return 'Faces left';
    case 'right': return view.note === 'mirror_duplicate' ? 'Faces right (its mirror is already in the gallery)' : 'Faces right (kept as uploaded)';
    case 'front': return 'Faces front';
    default: return 'Unclear';
  }
}

/** The chip tone of a facing: fine, something to look at, or not judged. */
export function facingTone(view: OutfitFacingView | undefined): 'ok' | 'warn' | 'neutral' | 'info' {
  if (!view) return 'neutral';
  if (view.facing === 'unclear') return 'warn';
  if (view.facing === 'right' && !view.mirrored) return 'warn';
  if (view.mirrored) return 'info';
  return 'ok';
}

/** Why the review list has an outfit, in a few words. */
export function facingNoteText(note: FacingNote | null): string {
  switch (note) {
    case 'model_failed': return 'the model call failed';
    case 'model_refused': return 'the model was not asked (rate or budget)';
    case 'mirror_duplicate': return 'its mirror is already in the gallery';
    case 'mirror_failed': return 'the image could not be mirrored';
    default: return 'the model could not tell';
  }
}

/** Whether the Unclear filter shows an outfit: judged unclear, or never judged. */
export function needsFacingReview(outfit: Pick<AdminOutfit, 'facing'>): boolean {
  return outfit.facing === undefined || outfit.facing.facing === 'unclear' || (outfit.facing.facing === 'right' && !outfit.facing.mirrored);
}

/** Add one batch's counts to the running total. */
export function addCounts(total: BackfillCounts, batch: BackfillCounts): BackfillCounts {
  return {
    scanned: total.scanned + batch.scanned,
    classified: total.classified + batch.classified,
    mirrored: total.mirrored + batch.mirrored,
    left: total.left + batch.left,
    right: total.right + batch.right,
    front: total.front + batch.front,
    unclear: total.unclear + batch.unclear,
    failed: total.failed + batch.failed,
  };
}

export const EMPTY_COUNTS: BackfillCounts = { scanned: 0, classified: 0, mirrored: 0, left: 0, right: 0, front: 0, unclear: 0, failed: 0 };

/** The backfill's running line: "Judged 50 of 692 · 12 mirrored · 2 unclear · 640 to go". */
export function backfillProgressText(counts: BackfillCounts, total: number, remaining: number): string {
  return `Judged ${counts.scanned.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} · ${counts.mirrored.toLocaleString('en-US')} mirrored · ${counts.unclear.toLocaleString('en-US')} unclear · ${counts.failed.toLocaleString('en-US')} failed · ${remaining.toLocaleString('en-US')} to go`;
}

/** The summary line: "692 outfits: 611 judged (430 face left, 98 mirrored, 61 front, 22 unclear), 81 not checked yet". */
export function facingSummaryText(summary: FacingSummary): string {
  const { counts } = summary;
  return `${summary.total.toLocaleString('en-US')} outfits: ${summary.judged.toLocaleString('en-US')} judged (${counts.left.toLocaleString('en-US')} face left, ${summary.mirrored.toLocaleString('en-US')} mirrored to face left, ${counts.front.toLocaleString('en-US')} face front, ${counts.unclear.toLocaleString('en-US')} unclear), ${summary.unjudged.toLocaleString('en-US')} not checked yet.`;
}
